import React, { useState } from "react";
import { Drawer, Table, Button, Space, Tag, Modal, Empty, Tooltip } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  UserOutlined,
} from "@ant-design/icons";
import DepartmentModal from "./DepartmentModal";

const DepartmentManagementDrawer = ({
  visible,
  onClose,
  departments,
  budgets,
  loading,
  onCreateDepartment,
  onUpdateDepartment,
  onDeleteDepartment,
  staffList,
  subjects = [],
}) => {
  const [departmentModalVisible, setDepartmentModalVisible] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Check if department has active budgets
  const hasActiveBudgets = (departmentId) => {
    return budgets.some(
      (b) =>
        b.departmentId === departmentId &&
        ["Active", "Approved", "Submitted"].includes(b.status),
    );
  };

  // Get budget count for department
  const getBudgetCount = (departmentId) => {
    return budgets.filter((b) => b.departmentId === departmentId).length;
  };

  const handleAdd = () => {
    setEditingDepartment(null);
    setDepartmentModalVisible(true);
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    setDepartmentModalVisible(true);
  };

  const handleDelete = (department) => {
    if (hasActiveBudgets(department.departmentId)) {
      Modal.warning({
        title: "Cannot Delete Item",
        content: `"${department.name}" has active, approved, or submitted budgets. Please close or delete those budgets first.`,
      });
      return;
    }

    Modal.confirm({
      title: "Delete Item",
      content: (
        <div>
          <p>
            Are you sure you want to delete <strong>{department.name}</strong>?
          </p>
          {getBudgetCount(department.departmentId) > 0 && (
            <p style={{ color: "#faad14" }}>
              This item has {getBudgetCount(department.departmentId)} budget(s)
              that will also be affected.
            </p>
          )}
        </div>
      ),
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        await onDeleteDepartment(department.departmentId);
      },
    });
  };

  const handleSubmit = async (data) => {
    setSubmitting(true);
    try {
      let result;
      if (editingDepartment) {
        result = await onUpdateDepartment(editingDepartment.departmentId, data);
      } else {
        result = await onCreateDepartment(data);
      }
      if (result) {
        setDepartmentModalVisible(false);
        setEditingDepartment(null);
        return true;
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Item",
      key: "department",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>{record.name}</span>
          <span style={{ color: "#888", fontSize: "12px" }}>{record.code}</span>
        </Space>
      ),
    },
    {
      title: "Item Owner",
      dataIndex: "headOfDepartmentName",
      key: "head",
      render: (name) =>
        name ? (
          <Space size="small">
            <UserOutlined style={{ color: "#1890ff" }} />
            <span>{name}</span>
          </Space>
        ) : (
          <span style={{ color: "#999" }}>Not assigned</span>
        ),
    },
    {
      title: "Subjects",
      key: "subjects",
      render: (_, record) =>
        Array.isArray(record.subjects) && record.subjects.length > 0 ? (
          <Space size={[4, 4]} wrap>
            {record.subjects.map((s) => (
              <Tag key={s.id}>{s.name || s.subject}</Tag>
            ))}
          </Space>
        ) : (
          <span style={{ color: "#999" }}>None</span>
        ),
    },
    {
      title: "Budgets",
      key: "budgets",
      width: 80,
      align: "center",
      render: (_, record) => {
        const count = getBudgetCount(record.departmentId);
        return count > 0 ? <Tag color="blue">{count}</Tag> : <Tag>0</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "status",
      width: 80,
      render: (isActive) => (
        <Tag color={isActive ? "green" : "default"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip
            title={
              hasActiveBudgets(record.departmentId)
                ? "Has active budgets"
                : "Delete"
            }
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              disabled={hasActiveBudgets(record.departmentId)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Drawer
        title={
          <Space>
            <BankOutlined />
            <span>Manage Items</span>
          </Space>
        }
        placement="right"
        onClose={onClose}
        open={visible}
        width={600}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Item
          </Button>
        }
      >
        {departments.length === 0 ? (
          <Empty
            description="No items yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={handleAdd}>
              Create First Item
            </Button>
          </Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={departments}
            rowKey="departmentId"
            size="small"
            pagination={false}
            loading={loading}
          />
        )}
      </Drawer>

      <DepartmentModal
        visible={departmentModalVisible}
        onCancel={() => {
          setDepartmentModalVisible(false);
          setEditingDepartment(null);
        }}
        onSubmit={handleSubmit}
        department={editingDepartment}
        loading={submitting}
        staffList={staffList}
        subjects={subjects}
      />
    </>
  );
};

export default DepartmentManagementDrawer;
